// Adapted from apps/admin/src/pages/dashboard/gps-tracking/components/gps-map-view.tsx (PRD #875):
// imports Leaflet's stylesheet itself, govdash having no global one.
import React, { useMemo, memo, useEffect } from "react";
import { MapContainer, TileLayer, Marker, Polyline, useMap, useMapEvents } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import type { LatLngExpression } from "leaflet";
import { MAP_TILE_CONFIG } from "@racoon/lib";
import { MapObject, MapObjectTooltip } from "~/features/map-view/ui/map-view";
import {
  makeIcon,
  makeTruckIconDynamic,
  pickupPalette,
  pinBadgeLabel,
} from "~/features/map-view/model/constants/icons";
import { AutoFitBounds } from "~/features/map-view/components/AutoFitBounds";
import { PickupGeofenceLayer } from "~/features/map-view/components/PickupGeofences";
import { StepPickupLayer } from "~/features/map-view/components/StepPickupLayer";
import { GpsTrackingPoint } from "../model/types";
import { ConnectProto } from "@racoon/api";
import { GpsStopsLayer, type GpsStopsLayerProps } from "~/features/gps-stops";
import { PhotoPointsLayer } from "~/features/map-view/components/PhotoPointsLayer";
import type { PhotoPoint } from "~/features/map-view/model/helpers/photo-points";
import { WholeStepWheelZoom } from "~/features/map-view/components/whole-step-wheel-zoom";

interface GpsMapViewProps {
  center: [number, number];
  zoom?: number;
  trucks: MapObject[];
  routePaths: Array<{ vehicleId: string; tripId?: string; path: LatLngExpression[] }>;
  selectedVehicleId?: string;
  selectedPointId?: string;
  onMarkerClick: (item: MapObject) => void;
  onPointClick?: (point: GpsTrackingPoint) => void;
  gpsPoints: GpsTrackingPoint[];
  pickupLocations?: ConnectProto.PickupLocation[];
  /** The routes' jobs as numbered step pins; drawn instead of plain `pickupLocations`. */
  stepPins?: MapObject[];
  depots?: ConnectProto.Location[];
  playbackPosition?: { lat: number; lon: number; speed: number; angle?: number } | null;
  autoFollow?: boolean;
  onAutoFollowChange?: (follow: boolean) => void;
  /** GPS dwell-event layer; omitted means no stops are drawn. */
  gpsStops?: GpsStopsLayerProps;
  photoPoints?: PhotoPoint[];
  /** Off inside the photo report, where a click would open the same report. */
  photosOpenReport?: boolean;
  /** The whole track to frame; `routePaths` is cut and extended during playback. */
  fitPath?: LatLngExpression[];
}

const MapFollowController = ({
  target,
  active,
  onUserInteraction,
}: {
  target: { lat: number; lon: number } | null;
  active: boolean;
  onUserInteraction: () => void;
}) => {
  const map = useMap();

  useMapEvents({
    dragstart: () => {
      onUserInteraction();
    },
  });

  useEffect(() => {
    if (active && target) {
      map.setView([target.lat, target.lon], map.getZoom(), { animate: true });
    }
  }, [active, target, map]);

  return null;
};

const NO_OBJECTS: MapObject[] = [];
// The player bar and its time chips, overlaid on the map's bottom edge.
const PLAYER_INSET = 72;

const GpsMapViewComponent: React.FC<GpsMapViewProps> = ({
  center,
  zoom = 13,
  trucks,
  routePaths,
  selectedVehicleId,
  selectedPointId,
  onMarkerClick,
  onPointClick,
  gpsPoints,
  pickupLocations = [],
  stepPins,
  depots = [],
  playbackPosition = null,
  autoFollow = false,
  onAutoFollowChange,
  gpsStops,
  photoPoints,
  photosOpenReport = true,
  fitPath,
}) => {
  const fitPaths = useMemo(() => (fitPath?.length ? [fitPath] : undefined), [fitPath]);

  const depotObjects = useMemo<MapObject[]>(
    () =>
      depots
        .filter((d) => d.lat && d.lon)
        .map((d) => ({
          id: `depot-${d.id}`,
          name: d.name,
          type: "depot" as MapObject["type"],
          lat: d.lat,
          lon: d.lon,
          locationType: d.type,
        })),
    [depots],
  );

  const latestPointByVehicle = useMemo(() => {
    const map = new Map<string | number, GpsTrackingPoint>();
    gpsPoints.forEach((p) => {
      const existing = map.get(p.vehicleId);
      if (!existing || p.timestamp > existing.timestamp) map.set(p.vehicleId, p);
    });
    return map;
  }, [gpsPoints]);

  return (
    <MapContainer
      center={center}
      zoom={zoom}
      scrollWheelZoom={true}
      className="h-full w-full"
      style={{
        height: "100%",
        width: "100%",
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
      }}
      doubleClickZoom
      markerZoomAnimation
      wheelDebounceTime={40}
      wheelPxPerZoomLevel={80}
      // Whole zooms can leave the fitted track filling half the frame; 0.25, not 0,
      // so wheel zooms still land near native tiles instead of permanently scaled ones.
      zoomSnap={0.25}
      zoomControl={false}
    >
      <WholeStepWheelZoom />
      <AutoFitBounds
        objects={NO_OBJECTS}
        trucks={trucks}
        routePaths={fitPaths}
        infoId={selectedVehicleId || null}
        bottomInset={PLAYER_INSET}
      />

      {playbackPosition && onAutoFollowChange && (
        <MapFollowController
          target={playbackPosition}
          active={autoFollow}
          onUserInteraction={() => onAutoFollowChange(false)}
        />
      )}

      <TileLayer attribution={MAP_TILE_CONFIG.attributionShort} url={MAP_TILE_CONFIG.url} />

      {/* Render route paths */}
      {routePaths.map((route, index) => (
        <Polyline
          key={`route-${route.vehicleId}-${route.tripId || index}`}
          positions={route.path}
          color="#000000"
          weight={3}
          opacity={1}
          lineCap="round"
          lineJoin="round"
        />
      ))}

      {/* GPS Stops */}
      {gpsStops && <GpsStopsLayer {...gpsStops} />}
      <PhotoPointsLayer points={photoPoints} opensReport={photosOpenReport} />

      {/* Truck Markers */}
      {trucks.map((truck) => {
        const isSelected =
          selectedVehicleId === truck.id.toString() || selectedVehicleId === truck.id;
        const latestPoint = latestPointByVehicle.get(truck.id);
        const isPointSelected = latestPoint && selectedPointId === latestPoint.id;

        const pos: [number, number] =
          playbackPosition && isSelected
            ? [playbackPosition.lat, playbackPosition.lon]
            : [truck.lat, truck.lon];

        const angle =
          playbackPosition && isSelected ? playbackPosition.angle || 0 : latestPoint?.angle || 0;
        const truckWithAngle = { ...truck, angle: angle - 90 };

        return (
          <Marker
            key={`truck-${truck.id}`}
            position={pos}
            icon={makeTruckIconDynamic(truckWithAngle, isSelected || isPointSelected)}
            zIndexOffset={isSelected ? 1000 : undefined}
            eventHandlers={{
              click: () => {
                onMarkerClick(truck);
                if (latestPoint && onPointClick) onPointClick(latestPoint);
              },
            }}
          />
        );
      })}

      {/* Pickup Locations */}
      {stepPins ? (
        <StepPickupLayer
          items={stepPins}
          colorFor={(o) => pickupPalette(o, false, o.jobStatus)}
          labelFor={pinBadgeLabel}
          renderMarker={(o) => (
            <Marker
              position={[o.lat, o.lon]}
              icon={makeIcon(o, false, false, undefined, o.jobStatus)}
              eventHandlers={{ click: () => o.onExpand?.() }}
            >
              <MapObjectTooltip item={o} />
            </Marker>
          )}
        />
      ) : (
        <PickupGeofenceLayer
          items={pickupLocations ?? []}
          colorFor={(loc) => pickupPalette(loc, false)}
          renderMarker={(loc) => (
            <Marker position={[loc.lat, loc.lon]} icon={makeIcon(loc, false)}>
              <MapObjectTooltip item={loc as unknown as MapObject} />
            </Marker>
          )}
        />
      )}

      {/* Depot Locations */}
      {depotObjects.map((depot) => (
        <Marker key={depot.id} position={[depot.lat, depot.lon]} icon={makeIcon(depot, false)}>
          <MapObjectTooltip item={depot} />
        </Marker>
      ))}
    </MapContainer>
  );
};

export const GpsMapView = memo(GpsMapViewComponent);
